import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UnifiedCalendarItem, CALENDAR_ITEM_COLORS, CALENDAR_ITEM_ICONS } from '../../../types/calendar';

interface EventPopoverProps {
  item: UnifiedCalendarItem;
  anchorRect: DOMRect;
  onClose: () => void;
}

const EventPopover: React.FC<EventPopoverProps> = ({ item, anchorRect, onClose }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const calculatePosition = () => {
      if (!popoverRef.current) return;

      const popover = popoverRef.current;
      const popoverRect = popover.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 12;

      let top = anchorRect.bottom + 8;
      let left = anchorRect.left;

      // Adjust if overflowing right
      if (left + popoverRect.width > viewportWidth - padding) {
        left = viewportWidth - popoverRect.width - padding;
      }

      // Adjust if overflowing left
      if (left < padding) {
        left = padding;
      }

      // Adjust if overflowing bottom - show above instead
      if (top + popoverRect.height > viewportHeight - padding) {
        top = anchorRect.top - popoverRect.height - 8;
      }

      // Make sure not above viewport
      if (top < padding) {
        top = padding;
      }

      setPosition({ top, left });
    };

    calculatePosition();

    // Close on scroll or resize
    const handleClose = () => onClose();
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);

    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [anchorRect, onClose]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatCurrency = (amount: number, currency: string = 'AUD') => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getIcon = () => {
    if (item.commitment) return item.icon || 'bi-calendar-event';
    switch (item.type) {
      case 'event': return 'bi-calendar-event';
      case 'invoice_due': return 'bi-receipt';
      case 'invoice_created': return 'bi-file-earmark-plus';
      case 'recurring_invoice':
      case 'recurring_expense': return 'bi-arrow-repeat';
      case 'bill_due': return 'bi-receipt';
      case 'job_due': return CALENDAR_ITEM_ICONS.job_due;
      default: return 'bi-calendar';
    }
  };

  const getTypeLabel = () => {
    if (item.commitment?.kind === 'support') return 'Monthly support';
    if (item.commitment?.kind === 'stripe') return item.commitment.estimated ? 'Stripe payment · estimated date' : 'Stripe payment · projected';
    switch (item.type) {
      case 'event': return 'Event';
      case 'invoice_due': return 'Invoice Due';
      case 'invoice_created': return 'Invoice Created';
      case 'recurring_invoice': return 'Recurring Income';
      case 'recurring_expense': return 'Recurring Expense';
      case 'bill_due': return 'Bill Due';
      case 'job_due': return 'Job Due';
      default: return 'Item';
    }
  };

  const getHeaderClass = () => {
    switch (item.type) {
      case 'event': return 'event';
      case 'invoice_due': return 'invoice';
      case 'invoice_created': return 'created';
      case 'recurring_invoice': return 'recurring';
      case 'recurring_expense': return 'expense';
      case 'bill_due': return 'bill';
      case 'job_due': return 'job';
      default: return 'event';
    }
  };

  const popoverContent = (
    <div
      ref={popoverRef}
      className="event-popover"
      style={{
        top: position.top,
        left: position.left,
      }}
      onMouseLeave={onClose}
    >
      {/* Header */}
      <div
        className={`event-popover__header event-popover__header--${getHeaderClass()}`}
        style={item.type === 'event' || item.commitment ? { background: item.color || CALENDAR_ITEM_COLORS.event } : undefined}
      >
        <div className="event-popover__icon">
          <i className={`bi ${getIcon()}`}></i>
        </div>
        <div className="event-popover__title">{item.title}</div>
      </div>

      {/* Body */}
      <div className="event-popover__body">
        {item.commitment && <div className="event-popover__row"><i className={`bi ${getIcon()}`}></i><span>{getTypeLabel()}</span></div>}
        <div className="event-popover__row">
          <i className="bi bi-calendar3"></i>
          <span>{formatDate(item.start)}</span>
        </div>

        {!item.allDay && (
          <div className="event-popover__row">
            <i className="bi bi-clock"></i>
            <span>{formatTime(item.start)}</span>
          </div>
        )}

        {item.customerName && (
          <div className="event-popover__row">
            <i className="bi bi-building"></i>
            <span>{item.customerName}</span>
          </div>
        )}

        {item.description && (
          <div className="event-popover__row">
            <i className="bi bi-text-left"></i>
            <span style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}>
              {item.description}
            </span>
          </div>
        )}
      </div>

      {/* Amount (for invoices) — bills are money OUT, so they share the expense treatment */}
      {item.amount !== undefined && item.amount > 0 && (
        <div className={`event-popover__amount event-popover__amount--${
          item.type === 'invoice_due' ? 'invoice' :
          item.type === 'invoice_created' ? 'created' :
          item.type === 'bill_due' ? 'bill' :
          item.type === 'recurring_expense' ? 'expense' : 'recurring'
        }`}>
          {item.type === 'recurring_expense' || item.type === 'bill_due' ? '-' : ''}{formatCurrency(item.amount, item.currencyCode)}
        </div>
      )}

      {/* Footer */}
      <div className="event-popover__footer">
        Click to view details
      </div>
    </div>
  );

  return createPortal(popoverContent, document.body);
};

export default EventPopover;
